import type {
  DeleteCallOffItemMutationOptions,
  DeleteCallOffItemVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysCallOff } from '../queryKeys';

import { deleteCallOffItem } from './deleteCallOffItem';

/**
 * Custom hook for soft deleting a line item from a call-off.
 *
 * @param options - Optional mutation options.
 * @returns An object containing the mutation function and related properties.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Offs/operation/delete_calloff_item_calloffs__calloff_id__items__item_id__delete
 */
export const useMutationDeleteCallOffItem = (
  options?: DeleteCallOffItemMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: DeleteCallOffItemVariables) =>
      deleteCallOffItem(variables),
    onSuccess(_data, variables) {
      // Invalidate the call-off items list
      queryClient.invalidateQueries({
        queryKey: queryKeysCallOff.getItems(variables.callOffId),
      });
      // Invalidate the call-off detail (since items are included in the response)
      queryClient.invalidateQueries({
        queryKey: queryKeysCallOff.get(variables.callOffId),
      });
    },
    ...options,
  });
};
